/**
 * The composer's suggestion menu (@ people, agents and broadcasts; # channels; : emoji; / commands). The Composer
 * shows it in a Popover above its field; the field keeps the keyboard (↑ ↓ Enter Tab Esc go to ChannelViewModel).
 */
import { Support, type AutocompleteItem, type AutocompleteMenuState } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import { Avatar, Icon, Pressable, Stack, Text } from "../primitives/index.ts";
import { useT, useTr } from "../i18n/I18nProvider.tsx";
import { AgentBadge, ExternalBadge } from "./Badges.tsx";
import { StatusEmoji } from "./StatusEmoji.tsx";

export type AutocompleteMenuProps = {
  menu: AutocompleteMenuState;
  onChoose: (index: number) => void;
  /** The pointer moved onto a row (it becomes the highlighted one). */
  onHover: (index: number) => void;
  testID: string;
};

export function AutocompleteMenu({ menu, onChoose, onHover, testID }: AutocompleteMenuProps) {
  const t = useT();
  const title =
    menu.kind === "mention"
      ? t("composer.autocomplete.people")
      : menu.kind === "channel"
        ? t("composer.autocomplete.channels")
        : menu.kind === "emoji"
          ? t("composer.autocomplete.emoji")
          : t("composer.autocomplete.commands");
  return (
    <Stack gap="0.5" padding="1" testID={testID}>
      <Stack paddingX="2" paddingY="1">
        <Text variant="caption" weight="semibold" color="text.tertiary">
          {title}
        </Text>
      </Stack>
      {menu.items.length === 0 ? (
        <Stack paddingX="2" paddingY="1.5" testID={`${testID}-empty`}>
          <Text variant="footnote" color="text.tertiary">
            {menu.query === "" ? t("composer.autocomplete.none") : t("composer.autocomplete.empty", { query: menu.query })}
          </Text>
        </Stack>
      ) : (
        <Stack gap="0.5" accessibilityRole="listbox" accessibilityLabel={title}>
          {menu.items.map((item, i) => (
            <Pressable
              key={`${item.kind}:${item.id}`}
              direction="horizontal"
              radius="sm"
              highlighted={i === menu.active}
              onPress={() => onChoose(i)}
              onHover={() => onHover(i)}
              testID={`${testID}-${item.kind}-${item.kind === "emoji" ? item.label : item.id}`}
            >
              <AutocompleteRow item={item} />
            </Pressable>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function AutocompleteRow({ item }: { item: AutocompleteItem }) {
  const tr = useTr();
  const t = useT();
  // Support's line says what it is ("Bundle's help agent"), in my language.
  const detail = item.kind === "user" && item.id === Support.SUPPORT_USER_ID ? t("support.mention.hint") : item.kind === "user" || item.kind === "channel" ? item.detail : undefined;
  return (
    <Stack grow direction="horizontal" align="center" gap="2.5" paddingX="2" paddingY="1.5">
      {item.kind === "user" ? (
        <Avatar name={item.label} src={item.avatarUrl} size="avatar.sm" square={item.isAgent} presence={item.presence} />
      ) : item.kind === "emoji" || item.kind === "workflow" ? (
        <Stack width="avatar.sm" align="center">
          {item.kind === "workflow" && item.icon === undefined ? <Icon name="sparkles" size="icon.sm" color="text.secondary" /> : <Text variant="body">{item.kind === "emoji" ? item.emoji : (item.icon ?? "")}</Text>}
        </Stack>
      ) : (
        <Stack padding="1" radius="sm" border="border.subtle" background="bg.canvas">
          <Icon
            name={item.kind === "channel" ? (item.isPrivate ? "lock" : "hash") : item.kind === "broadcast" ? "at" : item.kind === "appCommand" ? "code" : item.kind === "group" ? "users" : (item.icon as IconName)}
            size="icon.sm"
            color="text.secondary"
          />
        </Stack>
      )}
      <Stack grow direction="horizontal" align="center" gap="2">
        <Text variant="footnote" weight="semibold" lines={1}>
          {item.label}
        </Text>
        {item.kind === "user" && item.status !== undefined ? <StatusEmoji person={item} tooltip={false} /> : null}
        {item.kind === "user" && item.status !== undefined && item.status.text.trim() !== "" ? (
          // Slack: the status text instead of the name detail, so you know before you ping them.
          <Stack grow>
            <Text variant="footnote" color="text.tertiary" lines={1}>
              {item.status.text}
            </Text>
          </Stack>
        ) : item.kind === "user" || item.kind === "channel" ? (
          detail === undefined ? null : (
            <Stack grow>
              <Text variant="footnote" color="text.tertiary" lines={1}>
                {detail}
              </Text>
            </Stack>
          )
        ) : item.kind === "command" ? (
          item.usage === undefined ? null : (
            <Text variant="footnote" color="text.tertiary" lines={1}>
              {tr(item.usage)}
            </Text>
          )
        ) : item.kind === "appCommand" ? (
          item.usageHint === undefined ? null : (
            <Text variant="footnote" color="text.tertiary" lines={1}>
              {item.usageHint}
            </Text>
          )
        ) : item.kind === "broadcast" ? (
          <Stack grow>
            <Text variant="footnote" color="text.tertiary" lines={1}>
              {tr(item.description)}
            </Text>
          </Stack>
        ) : item.kind === "group" ? (
          <Stack grow direction="horizontal" gap="2">
            <Text variant="footnote" color="text.secondary" lines={1}>
              {item.name}
            </Text>
            <Text variant="footnote" color="text.tertiary" lines={1}>
              {tr({ key: "composer.group.members", params: { count: item.memberCount } })}
            </Text>
          </Stack>
        ) : null}
      </Stack>
      {item.kind === "command" ? (
        <Text variant="footnote" color="text.secondary" lines={1}>
          {tr(item.description)}
        </Text>
      ) : null}
      {item.kind === "appCommand" ? (
        <Text variant="footnote" color="text.secondary" lines={1}>
          {`${item.description} · ${item.appName}`}
        </Text>
      ) : null}
      {item.kind === "workflow" ? (
        <Text variant="footnote" color="text.secondary" lines={1}>
          {item.description === undefined ? tr({ key: "workflow.menu.badge", params: {} }) : `${item.description} · ${tr({ key: "workflow.menu.badge", params: {} })}`}
        </Text>
      ) : null}
      {item.kind === "user" && item.isAgent ? <AgentBadge /> : null}
      {item.kind === "user" && item.organization !== undefined ? <ExternalBadge organization={item.organization} /> : null}
      {item.kind === "channel" && item.external ? <ExternalBadge /> : null}
    </Stack>
  );
}
